import { type MobileVideoDownload } from "./mobileVideoStore";

type PickArgs = {
  // Resolved, webview-loadable local file src (already run through
  // Capacitor.convertFileSrc), or null when there's no cached poster.
  localUri: string | null;
  isOnline: boolean;
  // The video's online poster URL (videoPosterUrl — already absolute in
  // bundled native builds; never prepend a base to it), or null when the
  // video has no active poster.
  onlineUrl: string | null;
};

// Choose a poster image source. Online, always use the versioned public URL:
// it's the current active poster (cache-busted via ?v=), HTTP-cached after the
// first load, and avoids the local file going stale (active poster changed) or
// getting stuck on the placeholder if the cached file fails to load. Offline,
// fall back to the cached local file (the caller only passes one that still
// matches the active poster). Null → VideoPoster shows its placeholder.
export function pickPosterSrc({
  localUri,
  isOnline,
  onlineUrl,
}: PickArgs): string | null {
  if (isOnline) {
    return onlineUrl;
  }

  return localUri;
}

// True when a locally-cached poster is safe to show: a file is on disk AND it
// was cached for the video's current active poster. A mismatch means the active
// thumbnail changed server-side, so the local file is stale and we should fall
// back to the (fresh) online URL until a recache lands.
export function isCachedPosterFresh(
  download: MobileVideoDownload | null,
  activePosterId: string | null,
): boolean {
  return (
    download?.posterFilePath != null &&
    download.cachedPosterId === activePosterId
  );
}

// True when a completed download should (re)fetch its poster: there is an
// active poster, and we either haven't cached one or cached a now-stale one.
export function shouldCachePoster(
  download: MobileVideoDownload | null,
  activePosterId: string | null,
): boolean {
  if (!activePosterId) {
    return false;
  }

  if (download?.posterFilePath && download.cachedPosterId === activePosterId) {
    return false;
  }

  return true;
}
